import { useEffect, useState } from 'react';
import { changeOrderMsg, orderMsg, t } from '../../common/i18n/index.js';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { getDefaultDeliveryOrder } from '../../common/utils/orderUtils';
import { CompanyInfoResponse, CustomerOrderType } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { BreadCrumbsWithTitle } from '../BreadCrumbsWithTitle/BreadCrumbsWithTitle.js';
import { getCustomerOrderHeadingBottom, isSfdcOrder } from '../CustomerOrderList/customerOrderUtils';
import { DetailsWrapper } from '../DetailsWrapper/index.js';
import { Loading } from '../Loading/index.js';
import { CustomerOrderContent } from './CustomerOrderContent';
import type {
  BillingAccountSearchResponse,
  ChangeRequestDetails,
  ContactHeader,
  ContactSearchResponse,
  CustomerOrder,
  Subscription,
} from '../../generated/api/models.js';
import type { BreadCrumbList } from '../BreadCrumbs/index.js';

import './CustomerOrderDetails.scss';

export interface CustomerOrderDetailsProps {
  pathTemplate: string;
  contacts?: ContactSearchResponse[];
  deviceSubscriptions?: Subscription[];
  customerOrder?: CustomerOrder;
  changeRequestDetails?: ChangeRequestDetails;
  customerType?: CompanyInfoResponse.CustomerTypeEnum;
  firstBreadCrumbName?: string;
  billingAccounts?: BillingAccountSearchResponse[];
  mdmId: string;
  breadCrumbPaths?: BreadCrumbList;
  customerOrderDescription?: string;
}

export interface CustomerOrderDetailsFormValues {
  billingAccountId?: string;
  costCenter?: string;
  employeeNumber?: string;
}

export const CustomerOrderDetails = ({
  contacts,
  deviceSubscriptions,
  customerOrder,
  changeRequestDetails,
  breadCrumbPaths,
  customerType,
  pathTemplate,
  billingAccounts,
  mdmId,
  customerOrderDescription,
}: CustomerOrderDetailsProps) => {
  const [ordererContact, setOrdererContact] = useState<ContactHeader | undefined>(undefined);
  const { authenticatedUser } = useAuth();

  const companyName = getCompanyName(authenticatedUser, mdmId);

  useEffect(() => {
    const defaultDeliveryOrder = getDefaultDeliveryOrder(customerOrder?.deliveryOrders ?? []);
    const subContact = contacts
      ?.map(c => c.result)
      .find(cont => defaultDeliveryOrder?.userContactId === cont.contactId);
    setOrdererContact(subContact);
  }, [customerType, contacts, customerOrder]);

  const isInternalCustomer = customerType === CompanyInfoResponse.CustomerTypeEnum.INTERNAL_CUSTOMERS;

  if (!customerOrder && !changeRequestDetails) {
    return <Loading />;
  }

  const isOecOrder = customerOrder && !isSfdcOrder(customerOrder.customerOrderDisplayId);
  const isChangeRequest = isOecOrder
    ? customerOrder?.customerOrderType === CustomerOrderType.CHANGE_REQUEST
    : !customerOrder;
  const isOrder = !isChangeRequest;
  const id = customerOrder?.customerOrderDisplayId ?? changeRequestDetails?.changeRequestId ?? '…';
  const headingTop = isOrder ? t.C001(orderMsg) : undefined;
  const created = customerOrder?.created ?? changeRequestDetails?.created;
  const changeRequestId = isChangeRequest ? id : undefined;

  return (
    <DetailsWrapper
      classes={['of-customer-order-details']}
      detailsTop={breadCrumbPaths && <BreadCrumbsWithTitle breadCrumbPaths={breadCrumbPaths} />}
      id={`customer-order-details-${id}`}
      heading={isOrder ? id : t.PZ9O(changeOrderMsg)}
      headingBottom={getCustomerOrderHeadingBottom(created, changeRequestId, companyName)}
      headingTop={headingTop}
      heroPicto={isOrder ? 'order' : undefined}
    >
      <CustomerOrderContent
        pathTemplate={pathTemplate}
        customerOrder={customerOrder}
        changeRequestDetails={changeRequestDetails}
        ordererContact={ordererContact}
        deviceSubscriptions={deviceSubscriptions}
        isInternalCustomer={isInternalCustomer}
        billingAccounts={billingAccounts}
        mdmId={mdmId}
        customerOrderDescription={customerOrderDescription}
      />
    </DetailsWrapper>
  );
};
